import {
  componentOptions,
  type ComponentName,
  type ComponentState,
} from "app/componentOptions";
import type { RuntimeEnvironment } from "app/Firebase";
import {
  getScreenRouteBackSteps,
  getScreenRouteOwnerId,
} from "./screenRoute";

export const mobileComponentStackHistoryKey = "__jmMobileComponentStack";

type MobileComponentStackHistoryState = {
  [mobileComponentStackHistoryKey]?: true;
  stackDepth?: number;
  // Distance from Home to this screen's first entry, before its folder entries.
  historyIndex?: number;
};

function isRouteParamValue(value: unknown) {
  return (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean" ||
    value === null
  );
}

function appendRouteParam(
  params: URLSearchParams,
  key: string,
  value: unknown,
) {
  if (value === undefined) {
    return;
  }

  if (Array.isArray(value)) {
    value.forEach((item) => {
      if (isRouteParamValue(item)) {
        appendRouteParam(params, key, item);
      }
    });
    return;
  }

  if (!isRouteParamValue(value)) {
    return;
  }

  params.append(key, String(value));
}

function getComponentSearch(componentProps: unknown) {
  const params = new URLSearchParams();

  if (componentProps && typeof componentProps === "object") {
    Object.entries(componentProps).forEach(([key, value]) => {
      appendRouteParam(params, key, value);
    });
  }

  return params.toString();
}

function getComponentPathParts(componentState: ComponentState<ComponentName>) {
  const pathParts =
    componentOptions[componentState.componentType].routing
      ?.getPathParts?.({
        componentProps: componentState.componentProps,
      })
      .map((part) => encodeURIComponent(String(part ?? ""))) ?? [];

  // Interior empty segments reserve optional positions, such as a tab before folders.
  while (pathParts.at(-1) === "") {
    pathParts.pop();
  }

  return pathParts;
}

export function getMobileComponentStackBaseHref(
  environment: RuntimeEnvironment,
  tenantId: string | null,
) {
  if (environment === "DEVELOPMENT" && tenantId) {
    return `/?tenant=${encodeURIComponent(tenantId)}`;
  }

  return "/";
}

export function getMobileComponentStackRouteHref(
  componentState: ComponentState<ComponentName>,
  environment: RuntimeEnvironment,
  tenantId: string | null,
) {
  const pathParts = getComponentPathParts(componentState);
  const search =
    pathParts.length > 0 ? "" : getComponentSearch(componentState.componentProps);
  const componentPath = [
    encodeURIComponent(componentState.componentType),
    ...pathParts,
  ].join("/");

  if (environment === "DEVELOPMENT" && tenantId) {
    const params = new URLSearchParams(search);
    params.set("tenant", tenantId);

    return `/${componentPath}?${params.toString()}`;
  }

  return `/${componentPath}${search ? `?${search}` : ""}`;
}

export function getMobileComponentStackHistoryState(
  depth: number,
  historyIndex = depth,
) {
  return {
    [mobileComponentStackHistoryKey]: true,
    stackDepth: depth,
    historyIndex,
  } satisfies MobileComponentStackHistoryState;
}

export function getMobileComponentStackHistoryBackSteps(
  state: unknown,
  fallbackDepth = 0,
) {
  const depth = getMobileComponentStackHistoryDepth(state) ?? fallbackDepth;
  const index = isMobileComponentStackHistoryState(state)
    ? state.historyIndex
    : undefined;
  const historyIndex =
    typeof index === "number" && Number.isSafeInteger(index) && index >= 0
      ? index
      : depth;
  const ownerId = getScreenRouteOwnerId(state);
  const folderSteps =
    ownerId === null ? 0 : getScreenRouteBackSteps(state, ownerId) - 1;

  return historyIndex + folderSteps;
}

export function isMobileComponentStackHistoryState(
  state: unknown,
): state is MobileComponentStackHistoryState {
  return (
    typeof state === "object" &&
    state !== null &&
    (state as MobileComponentStackHistoryState)[
      mobileComponentStackHistoryKey
    ] === true
  );
}

export function getMobileComponentStackHistoryDepth(state: unknown) {
  if (!isMobileComponentStackHistoryState(state)) {
    return null;
  }

  const depth = (state as MobileComponentStackHistoryState).stackDepth;
  return typeof depth === "number" ? depth : null;
}
